import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

const WORKFLOW_STEPS = [
  { id: 1, title: 'Environment Setup', desc: 'Define organizational boundaries and operational control parameters.', icon: 'Settings' },
  { id: 2, title: 'Data Ingestion', desc: 'Connect utility APIs, fuel logs, and ERP systems to the data stream.', icon: 'Zap' },
  { id: 3, title: 'AI Classification', desc: 'Automatic mapping of activities to Scope 1, 2, or 3 categories.', icon: 'Cpu' },
  { id: 4, title: 'Supplier Outreach', desc: 'Automated surveys to collect primary data from Tier 1 partners.', icon: 'Users' },
  { id: 5, title: 'Gap Validation', desc: 'Identify data anomalies and secondary emission factors for missing logs.', icon: 'Search' },
  { id: 6, title: 'Disclosure Generation', desc: 'Produce audit-ready TCFD, GRI, and CSRD compliant reports.', icon: 'FileCheck' },
  { id: 7, title: 'Reduction Planning', desc: 'Set Science-Based Targets (SBTi) and track mitigation impact.', icon: 'TrendingDown' },
];

const SAMPLE_RECORDS = [
  { id: 'EF-2024-001', date: '2024-03-01', label: 'Main Office - Natural Gas', amount: '1,240.5', unit: 'm3', scope: 'Scope 1', status: 'Verified', note: 'Meter reading confirmed' },
  { id: 'EF-2024-002', date: '2024-03-05', label: 'Global Logistics - Air Freight', amount: '45.2', unit: 'tCO2e', scope: 'Scope 3', status: 'Pending', note: 'Awaiting supplier log' },
  { id: 'EF-2024-003', date: '2024-03-08', label: 'Data Center - Electricity', amount: '890.1', unit: 'kWh', scope: 'Scope 2', status: 'Verified', note: 'Renewable energy credit applied' },
  { id: 'EF-2024-004', date: '2024-03-12', label: 'Employee Commute - Survey', amount: '12.4', unit: 'tCO2e', scope: 'Scope 3', status: 'Error', note: 'Inconsistent distance values' },
  { id: 'EF-2024-005', date: '2024-03-15', label: 'Fleet Vehicle - Diesel', amount: '320.0', unit: 'Liters', scope: 'Scope 1', status: 'Verified', note: 'Fuel card integration' },
];

const FAQS = [
  { q: "How is the data 'verified' in CarbonFrame?", a: "Verification occurs through a dual-gate process: first, our AI flags anomalies against historical benchmarks; second, a human auditor reviews digital evidence logs attached to each record." },
  { q: "Can we import data from legacy ERP systems?", a: "Yes. CarbonFrame supports CSV/XLSX bulk imports and has pre-built API connectors for SAP, Oracle, and major utility providers." },
  { q: "Does this demo use real-time emission factors?", a: "This demo uses a snapshot of the 2024 DEFRA and IEA emission factor databases. In the live product, these are updated monthly." },
  { q: "How does the 'Supplier Tracker' handle non-responsive vendors?", a: "The system uses secondary data (industry averages) for missing entries while maintaining a 'Low Confidence' flag to encourage future primary data collection." },
  { q: "Is the report output compliant with CSRD?", a: "Absolutely. Our Report Builder includes templates specifically mapped to ESRS requirements under the CSRD directive." },
];

export default function DemoPage() {
  const [activeStep, setActiveStep] = useState(1);
  const [filter, setFilter] = useState('All');
  const [uiState, setUiState] = useState('success');
  const [openFaq, setOpenFaq] = useState(null);

  const filteredRecords = SAMPLE_RECORDS.filter(r => filter === 'All' || r.scope === filter);

  return (
    <div className="space-y-16 max-w-6xl mx-auto pb-24 px-2 md:px-0 overflow-x-hidden">
      
      {/* 1. Overview / What it does */}
      <section className="relative pt-8">
        <div className="absolute -top-24 -left-20 w-64 h-64 bg-emerald-500/5 rounded-full blur-[100px] pointer-events-none"></div>
        <div className="relative z-10 space-y-4">
          <h1 className="text-4xl md:text-5xl font-extrabold text-slate-100 tracking-tight leading-tight">
            Overview / <span className="text-emerald-400">What it does</span>
          </h1>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
            <p className="text-slate-400 text-lg leading-relaxed">
              CarbonFrame is a premium **B2B Sustainability Operating System** designed for mid-to-large enterprises. 
              We solve the fragmented data problem in ESG reporting by centralizing emissions metrics into a 
              single, audit-ready "Analytics Lab."
            </p>
            <div className="glass-panel p-6 rounded-2xl border-emerald-500/20 bg-emerald-500/5">
              <h4 className="text-emerald-400 font-bold text-sm uppercase tracking-widest mb-2">The Outcome</h4>
              <p className="text-slate-300 text-sm">
                Transform raw utility bills and supplier spreadsheets into compliant disclosures (TCFD/GRI) 
                while identifying high-impact reduction opportunities in your value chain.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Demo / How It Works */}
      <section className="space-y-8">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center text-cyan-400 border border-cyan-500/20">
            <SafeIcon name="Activity" />
          </div>
          <h2 className="text-2xl font-bold text-slate-100 italic tracking-tight">Demo / How It Works</h2>
        </div>

        <div className="space-y-6">
          <div className="flex items-center gap-2 text-emerald-400 uppercase tracking-widest text-[10px] font-bold">
            <div className="w-1 h-1 bg-emerald-400 rounded-full animate-pulse"></div>
            Try this flow
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Workflow Navigation */}
            <div className="lg:col-span-4 space-y-2 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
              {WORKFLOW_STEPS.map((step) => (
                <button
                  key={step.id}
                  onClick={() => setActiveStep(step.id)}
                  className={`w-full text-left p-4 rounded-xl transition-all duration-300 border flex gap-3 items-start group ${
                    activeStep === step.id 
                      ? 'bg-emerald-500/10 border-emerald-500/40 shadow-lg' 
                      : 'bg-slate-900/40 border-slate-800 hover:border-slate-700'
                  }`}
                >
                  <span className={`text-xs font-mono mt-1 ${activeStep === step.id ? 'text-emerald-400' : 'text-slate-600'}`}>0{step.id}</span>
                  <div className="flex-1">
                    <h4 className={`font-bold text-sm ${activeStep === step.id ? 'text-slate-100' : 'text-slate-400 group-hover:text-slate-200'}`}>{step.title}</h4>
                    <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-1">{step.desc}</p>
                  </div>
                  {activeStep === step.id && <SafeIcon name="ChevronRight" className="text-emerald-400 w-4 h-4 mt-1" />}
                </button>
              ))}
            </div>

            {/* Visual Workspace Simulation */}
            <div className="lg:col-span-8 glass-panel rounded-2xl p-6 md:p-10 flex flex-col items-center justify-center min-h-[400px] relative overflow-hidden">
              <div className="absolute inset-0 bg-grid-slate-800/[0.05] bg-[size:20px_20px]"></div>
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeStep}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="text-center space-y-6 z-10"
                >
                  <div className="relative inline-block">
                    <div className="absolute inset-0 bg-emerald-500/30 blur-3xl rounded-full"></div>
                    <div className="relative p-8 rounded-3xl bg-slate-900 border border-slate-800 shadow-2xl">
                      <SafeIcon name={WORKFLOW_STEPS[activeStep-1].icon} className="w-12 h-12 text-emerald-400" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-xl font-bold text-slate-100">Step {activeStep}: {WORKFLOW_STEPS[activeStep-1].title}</h3>
                    <p className="text-slate-400 text-sm max-w-sm mx-auto leading-relaxed">
                      {WORKFLOW_STEPS[activeStep-1].desc}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-3 justify-center pt-4">
                    <button className="px-5 py-2 bg-emerald-500 text-slate-950 font-bold rounded-lg text-xs hover:bg-emerald-400 transition-all shadow-lg shadow-emerald-500/20">Execute Workflow</button>
                    <button className="px-5 py-2 border border-slate-700 text-slate-300 rounded-lg text-xs hover:bg-slate-800 transition-all">View Logs</button>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Sample Data & 4. Simulated States */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        
        {/* Sample Data */}
        <section className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-slate-200 flex items-center gap-2">
              <SafeIcon name="Database" className="text-cyan-400 w-4 h-4" /> Sample Data
            </h3>
            <div className="flex bg-slate-900 border border-slate-800 rounded-md p-1">
              {['All', 'Scope 1', 'Scope 2', 'Scope 3'].map(opt => (
                <button
                  key={opt}
                  onClick={() => setFilter(opt)}
                  className={`px-3 py-1 text-[9px] font-bold rounded transition-all ${filter === opt ? 'bg-cyan-500 text-slate-950 shadow-sm' : 'text-slate-500 hover:text-slate-300'}`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          <div className="glass-panel rounded-xl overflow-hidden border-slate-800/50">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-[11px]">
                <thead className="bg-slate-900/50 border-b border-slate-800 text-slate-500 uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Activity</th>
                    <th className="px-4 py-3 font-semibold text-right">Amount</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/40">
                  {filteredRecords.map(rec => (
                    <tr key={rec.id} className="hover:bg-slate-800/20 transition-colors group">
                      <td className="px-4 py-4">
                        <p className="font-bold text-slate-200">{rec.label}</p>
                        <p className="text-[9px] text-slate-500 font-mono mt-0.5">{rec.id} • {rec.date}</p>
                      </td>
                      <td className="px-4 py-4 text-right font-mono">
                        <span className="text-slate-100">{rec.amount}</span> <span className="text-slate-600">{rec.unit}</span>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold border ${
                          rec.status === 'Verified' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
                          rec.status === 'Pending' ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' :
                          'bg-rose-500/10 text-rose-400 border-rose-500/20'
                        }`}>
                          {rec.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Simulated States */}
        <section className="space-y-6">
          <h3 className="text-lg font-bold text-slate-200 flex items-center gap-2">
            <SafeIcon name="Layers" className="text-amber-400 w-4 h-4" /> Simulated States
          </h3>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {['success', 'loading', 'empty', 'error'].map(state => (
              <button
                key={state}
                onClick={() => setUiState(state)}
                className={`p-3 rounded-xl border text-center transition-all ${
                  uiState === state ? 'bg-slate-800 border-slate-600 ring-2 ring-emerald-500/20 shadow-xl' : 'bg-slate-900/40 border-slate-800 hover:border-slate-700'
                }`}
              >
                <div className="h-10 flex items-center justify-center mb-2">
                  {state === 'loading' && <div className="w-4 h-4 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>}
                  {state === 'success' && <SafeIcon name="CheckCircle" className="text-emerald-500" />}
                  {state === 'error' && <SafeIcon name="AlertCircle" className="text-rose-500" />}
                  {state === 'empty' && <SafeIcon name="Inbox" className="text-slate-700" />}
                </div>
                <span className="text-[9px] uppercase font-bold text-slate-400 block">{state}</span>
              </button>
            ))}
          </div>

          <div className="glass-panel p-6 rounded-xl border-dashed border-slate-700 bg-slate-950/50">
            <AnimatePresence mode="wait">
              {uiState === 'success' && (
                <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} className="flex items-start gap-4">
                  <div className="w-10 h-10 shrink-0 rounded-lg bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20 text-emerald-400">
                    <SafeIcon name="Check" />
                  </div>
                  <div>
                    <h5 className="text-sm font-bold text-emerald-400">System Nominal</h5>
                    <p className="text-xs text-slate-500 leading-relaxed mt-1">Audit trail logged for FY24 Q1. No discrepancies found in utility data stream integration.</p>
                  </div>
                </motion.div>
              )}
              {uiState === 'loading' && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-3">
                  <div className="h-1.5 w-full bg-slate-800 rounded-full overflow-hidden">
                    <motion.div 
                      className="h-full bg-cyan-500" 
                      initial={{ x: '-100%' }} 
                      animate={{ x: '100%' }} 
                      transition={{ repeat: Infinity, duration: 1.5, ease: "linear" }}
                    />
                  </div>
                  <p className="text-[10px] text-slate-500 italic flex items-center gap-2">
                    <SafeIcon name="RefreshCw" className="w-3 h-3 animate-spin" /> Fetching real-time grid intensity factors...
                  </p>
                </motion.div>
              )}
              {uiState === 'empty' && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-center py-2">
                  <p className="text-xs text-slate-600 font-medium">No reduction initiatives found for "Transport" category.</p>
                  <button className="text-[10px] text-cyan-400 mt-2 underline">Create your first project</button>
                </motion.div>
              )}
              {uiState === 'error' && (
                <motion.div initial={{ opacity: 0, x: -5 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} className="flex items-start gap-4">
                  <div className="w-10 h-10 shrink-0 rounded-lg bg-rose-500/10 flex items-center justify-center border border-rose-500/20 text-rose-400">
                    <SafeIcon name="X" />
                  </div>
                  <div>
                    <h5 className="text-sm font-bold text-rose-400">Module Timeout</h5>
                    <p className="text-xs text-slate-500 leading-relaxed mt-1">The ERP gateway did not respond within 5000ms. Check your [Environment_Token] in settings.</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </section>
      </div>

      {/* 5. FAQ */}
      <section className="space-y-8">
        <h3 className="text-2xl font-bold text-slate-100 flex items-center gap-2">
          <SafeIcon name="HelpCircle" className="text-emerald-400" /> FAQ / Support
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {FAQS.map((faq, idx) => (
            <div 
              key={idx} 
              className={`glass-panel rounded-xl overflow-hidden transition-all duration-300 ${openFaq === idx ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-slate-800'}`}
            >
              <button 
                onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                className="w-full text-left p-5 flex justify-between items-center gap-4 group"
              >
                <span className="text-sm font-semibold text-slate-200 group-hover:text-emerald-400 transition-colors">{faq.q}</span>
                <SafeIcon name={openFaq === idx ? "ChevronUp" : "ChevronDown"} className="w-4 h-4 text-slate-500 shrink-0" />
              </button>
              <AnimatePresence>
                {openFaq === idx && (
                  <motion.div 
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="p-5 pt-0 text-xs text-slate-400 leading-relaxed border-t border-slate-800/50">
                      {faq.a}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </section>
      
      {/* Footer Credit */}
      <div className="pt-12 flex flex-col md:flex-row gap-4 justify-between items-center border-t border-slate-900 text-slate-600">
        <p className="text-[10px] font-mono tracking-tighter">[ BUILD_ID: CARBONFRAME-B2B-TOUR-99 ]</p>
        <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">
          Made by LingLink AI and DigLab AI
        </p>
      </div>
    </div>
  );
}